import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Table, Button } from "react-bootstrap"
import TextInput from "@/components/input/Text"
import NumberInput from "@/components/input/Number"
import { useModal } from "@/helpers/useModal"
import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import { getView, getShowBulkEditForm } from "../selectors"
import { onCancelBulkEdit, setShowBulkEditForm, appendBulkDepartures } from "../Slice"
import ReasonModal from "./ReasonModal"

const AppendDepartures = (): JSX.Element => {
  const view = useSelector(getView)
  const showBulkEditForm = useSelector(getShowBulkEditForm)
  const dispatch = useDispatch()
  const [header, setHeader] = useState("")
  const [reasonPrompt, setReasonPrompt] = useState("")

  const [localDepartures, setLocalDepartures] = useState([
    {
      departureTime: "",
      durationInMinutes: view.inventory.tourDuration || 0,
      minimumCapacity: 0,
      maximumCapacity: 0,
    },
  ])

  const onBulkDepartureSubmitCallback = async (reason) => {
    const dates = view.inventory.allocationTableData.callDays.map((callDay) => callDay.date)
    const bulkPayload = { departures: localDepartures, dates, reason }
    dispatch(setShowBulkEditForm({ showFormState: !showBulkEditForm }))
    dispatch(onCancelBulkEdit())
    await dispatch(appendBulkDepartures(bulkPayload))
  }

  const toggleSubmitBulkDeparturesModal = useModal((props) => (
    <ReasonModal
      reasonPrompt={reasonPrompt}
      buttonText="Submit"
      onSubmitCallback={onBulkDepartureSubmitCallback}
      header={header}
      {...props}
    />
  ))

  const toggleCruiselineSubmitBulkDeparturesModal = useModal((props) => (
    <ReasonModal
      reasonPrompt={reasonPrompt}
      buttonText="Request"
      onSubmitCallback={onBulkDepartureSubmitCallback}
      header={header}
      {...props}
    />
  ))

  const onSubmit = () => {
    setHeader("You are allocating additional departures in bulk")
    setReasonPrompt("Please provide a reason for this submission")
    toggleSubmitBulkDeparturesModal()
  }

  const onCruiselineSubmit = () => {
    setHeader("You are requesting additional departures in bulk")
    setReasonPrompt("Please provide a reason for these additional requests")
    toggleCruiselineSubmitBulkDeparturesModal()
  }

  return (
    showBulkEditForm && (
      <>
        <div className="mt-3">
          <CruiseLineOnly>
            <h5 className="d-inline">Append departure requests in bulk</h5>
            <i
              title="On submit these departure requests will be appended to all the resulting days in the allocation table below. Existing requests will remain."
              className="fas fa-info-circle d-inline ms-2 cursor-pointer"
            />
          </CruiseLineOnly>
          <OperatorOnly>
            <h5 className="d-inline">Append departures in bulk</h5>
            <i
              title="On submit these departures will be appended to all the resulting days in the allocation table below. Existing departures will remain."
              className="fas fa-info-circle d-inline ms-2 cursor-pointer"
            />
          </OperatorOnly>
        </div>
        <div style={{ width: "50%" }}>
          <Table className="mt-2">
            <thead className="thead-light">
              <tr>
                <th>Departure</th>
                <th>Duration</th>
                <th>Min</th>
                <th>Max</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {localDepartures.map((departure, index) => (
                    <div className="d-flex align-items-center" key={index}>
                      {localDepartures.length !== 1 && (
                        <div
                          className="cursor-pointer mt-n1"
                          onClick={() =>
                            setLocalDepartures(localDepartures.filter((_, i) => i !== index))
                          }
                        >
                          <i className="fas fa-window-close fa-lg me-3 ms-n4" />
                        </div>
                      )}
                      <TextInput
                        style={{ maxWidth: "120px", minWidth: "90px" }}
                        className="mb-2"
                        name="departureTime"
                        label=""
                        placeholder="00:00"
                        value={departure.departureTime}
                        onChange={(e) => {
                          const newDepartures = [...localDepartures]
                          newDepartures[index].departureTime = e.target.value
                          setLocalDepartures(newDepartures)
                        }}
                      />
                    </div>
                  ))}
                </td>
                <td>
                  {localDepartures.map((departure, index) => (
                    <NumberInput
                      key={index}
                      style={{ maxWidth: "100px", minWidth: "90px" }}
                      suffix="min"
                      className="mb-2"
                      name="durationInMinutes"
                      label=""
                      placeholder={0}
                      value={departure.durationInMinutes}
                      onChange={(e) => {
                        const newDepartures = [...localDepartures]
                        newDepartures[index].durationInMinutes = e.target.value
                        setLocalDepartures(newDepartures)
                      }}
                    />
                  ))}
                </td>
                <td>
                  {localDepartures.map((departure, index) => (
                    <NumberInput
                      key={index}
                      style={{ maxWidth: "100px", minWidth: "90px" }}
                      className="mb-2"
                      name="minimumCapacity"
                      label=""
                      placeholder={0}
                      value={departure.minimumCapacity}
                      onChange={(e) => {
                        const newDepartures = [...localDepartures]
                        newDepartures[index].minimumCapacity = e.target.value
                        setLocalDepartures(newDepartures)
                      }}
                    />
                  ))}
                </td>
                <td>
                  {localDepartures.map((departure, index) => (
                    <NumberInput
                      key={index}
                      style={{ maxWidth: "100px", minWidth: "90px" }}
                      className="mb-2"
                      name="maximumCapacity"
                      label=""
                      placeholder={0}
                      value={departure.maximumCapacity}
                      onChange={(e) => {
                        const newDepartures = [...localDepartures]
                        newDepartures[index].maximumCapacity = e.target.value
                        setLocalDepartures(newDepartures)
                      }}
                    />
                  ))}
                  <Button
                    className="btn-sm btn-outline-light mt-1 ms-6"
                    onClick={() => {
                      const last = localDepartures[localDepartures.length - 1] || {}
                      const newRow = {
                        departureTime: last.departureTime || "",
                        durationInMinutes: last.durationInMinutes || 0,
                        minimumCapacity: last.minimumCapacity || 0,
                        maximumCapacity: last.maximumCapacity || 0,
                      }
                      setLocalDepartures([...localDepartures, newRow])
                    }}
                  >
                    <i className="fas fa-plus cursor-pointer" />
                  </Button>
                </td>
                <td>
                  <CruiseLineOnly>
                    <Button variant="primary" onClick={onCruiselineSubmit}>
                      Submit
                    </Button>
                  </CruiseLineOnly>
                  <OperatorOnly>
                    <Button variant="primary" onClick={onSubmit}>
                      Submit
                    </Button>
                  </OperatorOnly>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </>
    )
  )
}

export default AppendDepartures
